import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { matchRequest } from "./utils";

const initialState: State = [];

function handleRequest(
  state: State,
  {
    scope,
    startDate,
    endDate,
  }: Boundaries["retrieveWeekTimeRecords"]["request"],
): State {
  const index = state.findIndex((req) =>
    matchRequest(req, scope, startDate, endDate),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          scope,
          startDate,
          endDate,
          timeRecords: [],
          isDirty: false,
          request: setRequestStateToPending(),
        },
      ];
}

function handleSuccess(
  state: State,
  {
    scope,
    startDate,
    endDate,
    timeRecords,
  }: Boundaries["retrieveWeekTimeRecords"]["success"],
): State {
  const index = state.findIndex((req) =>
    matchRequest(req, scope, startDate, endDate),
  );
  const nextReq = {
    scope,
    startDate,
    endDate,
    timeRecords,
    isDirty: false,
    request: setRequestStateToSuccess(),
  };

  return index !== -1
    ? [...state.slice(0, index), nextReq, ...state.slice(index + 1)]
    : [...state, nextReq];
}

function handleFailure(
  state: State,
  {
    scope,
    startDate,
    endDate,
  }: Boundaries["retrieveWeekTimeRecords"]["failure"],
): State {
  const index = state.findIndex((req) =>
    matchRequest(req, scope, startDate, endDate),
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          isDirty: false,
          request: setRequestStateToFailure(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          scope,
          startDate,
          endDate,
          timeRecords: [],
          isDirty: false,
          request: setRequestStateToFailure(),
        },
      ];
}

function markAsDirty(state: State): State {
  return state.map((req) => ({ ...req, isDirty: true }));
}

function handleDeleteTimeRecord(
  state: State,
  { timeRecord }: Boundaries["deleteTimeRecord"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    timeRecords: req.timeRecords.filter(({ uuid }) => uuid !== timeRecord.uuid),
  }));
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveWeekTimeRecordsRequested]: handleRequest,
  [events.retrieveWeekTimeRecordsSucceeded]: handleSuccess,
  [events.retrieveWeekTimeRecordsFailed]: handleFailure,
  [events.saveTimeRecordSucceeded]: markAsDirty,
  [events.validateTimeRecordsUpToDateSucceeded]: markAsDirty,
  [events.validatePersonalTimeRecordsUpToDateSucceeded]: markAsDirty,
  [events.deleteTimeRecordSucceeded]: handleDeleteTimeRecord,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
